/**
 * Tiny IndexedDB key/value store for persisting a loaded .vizproject across
 * browser refreshes. localStorage is too small (embedded video base64 easily
 * exceeds its ~5MB quota), so the raw project JSON is stashed in IndexedDB and
 * re-parsed on startup to rebuild the media object-URLs + the live engine.
 */

const DB_NAME = 'synapse-viz';
const STORE = 'kv';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function idbSet(key: string, value: unknown): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function idbGet<T = unknown>(key: string): Promise<T | null> {
  const db = await openDb();
  const out = await new Promise<T | null>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve((req.result as T) ?? null);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return out;
}

export async function idbDel(key: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

/** Legacy single-project key (kept for migration). */
export const VIZPROJECT_KEY = 'flow:vizproject';

/** Visualizer library: a metadata index + one raw-JSON entry per imported item
 *  + the active item's id. */
export const VIZLIB_INDEX_KEY = 'flow:vizlib:index';
export const VIZLIB_ACTIVE_KEY = 'flow:vizlib:active';
export const vizlibRawKey = (id: string) => `flow:vizlib:raw:${id}`;
